import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import savsService from '../../services/savsService';
import { formatPrice } from '../../utils/orderUtils';

const PROBLEM_OPTIONS = [
  'Produit défectueux',
  'Produit reçu endommagé',
  'Produit non conforme',
  'Produit manquant',
  'Taille incorrecte',
  'Couleur incorrecte',
  'Problème de qualité',
  'Autre',
];

const ACTION_OPTIONS = [
  { value: 'remboursement', label: 'Remboursement' },
  { value: 'changement', label: 'Changement' },
  { value: 'avoir', label: 'Avoir' },
  { value: 'reparation', label: 'Réparation' },
  { value: 'autre', label: 'Autre' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'non_traite':
      return 'warning';
    case 'en_cours':
      return 'info';
    case 'fini':
      return 'success';
    default:
      return 'default';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'non_traite':
      return 'Non traité';
    case 'en_cours':
      return 'En cours';
    case 'fini':
      return 'Fini';
    default:
      return status;
  }
};

const getActionLabel = (action) => {
  const option = ACTION_OPTIONS.find((opt) => opt.value === action);
  return option ? option.label : action;
};

const SavDetailsModal = ({ open, onClose, sav, onUpdate, onDelete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    reason: '',
    problem: '',
    productId: '',
    action: 'remboursement',
    notes: '',
    status: 'non_traite',
  });

  React.useEffect(() => {
    if (sav) {
      setFormData({
        reason: sav.reason || '',
        problem: sav.problem || '',
        productId: sav.productId || '',
        action: sav.action || 'remboursement',
        notes: sav.notes || '',
        status: sav.status || 'non_traite',
      });
      setIsEditing(false);
    }
  }, [sav]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError(null);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        reason: formData.reason,
        problem: formData.problem,
        productId: formData.productId ? parseInt(formData.productId) : null,
        action: formData.action,
        notes: formData.notes || null,
        status: formData.status,
      };

      await savsService.updateSav(sav.id, payload);
      setIsEditing(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour du SAV');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce SAV ?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await savsService.deleteSav(sav.id);
      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression du SAV');
    } finally {
      setLoading(false);
    }
  };

  if (!sav) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Détails du SAV #{sav.id}</Typography>
          <Chip
            label={getStatusLabel(sav.status)}
            color={getStatusColor(sav.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Informations de la commande */}
        <Card sx={{ mb: 2, backgroundColor: 'background.paper' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Informations de la commande
            </Typography>
            {sav.order && (
              <>
                <Typography variant="body2">
                  <strong>N° de commande:</strong> {sav.order.orderNumber}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{' '}
                  {new Date(sav.order.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Client:</strong>{' '}
                  {sav.order.user
                    ? `${sav.order.user.first_name} ${sav.order.user.last_name}`
                    : `${sav.order.billingFirstName} ${sav.order.billingLastName}`}
                </Typography>
                <Typography variant="body2">
                  <strong>Total:</strong> {formatPrice(sav.order.totalAmount || 0)}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Raison du SAV"
              value={formData.reason}
              onChange={handleChange('reason')}
              multiline
              rows={2}
              disabled={!isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth required disabled={!isEditing}>
              <InputLabel>Problème</InputLabel>
              <Select
                value={formData.problem}
                onChange={handleChange('problem')}
                label="Problème"
              >
                {PROBLEM_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {sav.product && (
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Produit concerné
                  </Typography>
                  <Typography variant="body2">{sav.product.name}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required disabled={!isEditing}>
              <InputLabel>Action</InputLabel>
              <Select
                value={formData.action}
                onChange={handleChange('action')}
                label="Action"
              >
                {ACTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!isEditing}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Statut"
              >
                <MenuItem value="non_traite">Non traité</MenuItem>
                <MenuItem value="en_cours">En cours</MenuItem>
                <MenuItem value="fini">Fini</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes (ce qui a été fait)"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={4}
              disabled={!isEditing}
              placeholder="Décrivez ce qui a été fait sur ce SAV..."
            />
          </Grid>

          {sav.creator && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Créé par: {sav.creator.first_name} {sav.creator.last_name} le{' '}
                {new Date(sav.createdAt).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDelete}
          color="error"
          startIcon={<DeleteIcon />}
          disabled={loading}
        >
          Supprimer
        </Button>
        <Box flex={1} />
        <Button onClick={onClose} disabled={loading}>
          Fermer
        </Button>
        {isEditing ? (
          <>
            <Button
              onClick={() => {
                setIsEditing(false);
                // Reset form data
                setFormData({
                  reason: sav.reason || '',
                  problem: sav.problem || '',
                  productId: sav.productId || '',
                  action: sav.action || 'remboursement',
                  notes: sav.notes || '',
                  status: sav.status || 'non_traite',
                });
              }}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              Enregistrer
            </Button>
          </>
        ) : (
          <Button onClick={() => setIsEditing(true)} variant="contained">
            Modifier
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SavDetailsModal;
